/**
 * 교사·원장 대상 학교·학년 자료 제공 패널(PR36 beta). teacher-ops 화면 아래에 붙는다.
 * 대상은 (학교, 학년) 쌍을 하나씩 추가해 만든다(학교 여러 개 × 학년 여러 개의 곱집합을 만들지 않는다).
 * 요청 키: 결과를 못 받은 생성 요청은 같은 키·같은 내용으로만 다시 보낸다. 확정 응답(HTTP) 뒤에만 키를 버리고 충돌에 새 키를 만들지 않는다.
 * 목록에는 학생 이름·계정을 넣지 않고 인원 수만 보인다. 학생 배포 허용은 바뀌지 않는다(서버가 정한다).
 */
import { h, clear, toast } from './dom.js';
import { currentGen, newRequestKey, type Transport } from '../core/transport.js';
import { mountSchoolGradePicker, type Pair } from './school-grade-picker.js';

interface Rel { id: string; title: string; version: number }
interface TargetRow { targetId: string; title: string; closed: boolean; includedStudents: number; pairs: Pair[] }

export function mountTargetPanel(root: HTMLElement, t: Transport, role: 'director' | 'teacher' = 'director'): void {
  const gen = currentGen();
  const live = () => gen === currentGen() && root.isConnected;
  const msg = h('p', { role: 'status', 'data-testid': 'tp-msg' });
  const say = (s: string) => { msg.textContent = s; };
  let pendingKey: { id: string; key: string } | null = null;
  const relSel = h('select', { 'data-testid': 'tp-release' });
  const list = h('div', { 'data-testid': 'tp-targets' });
  // 학교마다 학년 토글을 두고 켠 (학교, 학년) 쌍만 담는다(PDF 등록과 같은 공용 선택기, 곱집합 없음)
  const picker = mountSchoolGradePicker(t, live, () => { pendingKey = null; say(''); });
  const nameOf = picker.nameOf;

  // 학교 추가는 서버가 권한 있는 계정만 허용한다. 학년 1~3은 서버가 만들고 같은 이름은 거부된다.
  const newName = h('input', { type: 'text', maxlength: '100', 'aria-label': '새 학교 이름', 'data-testid': 'tp-new-name' });
  const newLevel = h('select', { 'aria-label': '학교급', 'data-testid': 'tp-new-level' },
    h('option', { value: 'middle' }, '중학교'), h('option', { value: 'high' }, '고등학교'), h('option', { value: 'elementary' }, '초등학교'));
  const addSchool = h('button', { type: 'button', 'data-testid': 'tp-add-school', onclick: async () => {
    const name = newName.value.trim();
    if (name === '') { say('학교 이름을 입력해 주세요.'); return; }
    const r = await t.request('POST', '/admin/schools', { body: { name, level: newLevel.value } });
    if (!live()) return;
    if (r.kind === 'network') { say('결과를 받지 못했어요. 목록을 확인한 뒤 다시 시도해 주세요.'); return; }
    if (r.status === 201) { newName.value = ''; toast('학교를 추가했어요.'); if (!(await picker.reload())) say('학교 목록을 다시 불러오지 못했어요.'); return; }
    say(r.status === 403 ? '학교를 추가할 권한이 없어요.' : r.status === 409 ? '같은 이름의 학교가 이미 있어요.' : `추가하지 못했어요(${String(r.body['error'] ?? r.status)}).`);
  } }, '+ 학교 추가');

  async function loadTargets(): Promise<void> {
    const r = await t.request('GET', '/admin/material-targets');
    if (!live()) return;
    clear(list);
    if (r.kind !== 'http' || r.status !== 200) { say('제공 목록을 불러오지 못했어요.'); return; }
    const rows = r.body['targets'] as TargetRow[];
    list.append(h('h3', {}, '제공 중인 대상'), rows.length === 0 ? h('p', { 'data-testid': 'tp-empty' }, '아직 만든 대상이 없어요.') : h('ul', {}, ...rows.map((x) =>
      h('li', { 'data-testid': `tp-target-${x.targetId}` }, `${x.title} · ${x.pairs.map((p) => `${nameOf(p.schoolId)} ${p.grade}학년`).join(', ')} · 포함 학생 ${x.includedStudents}명${x.closed ? ' · 닫힘' : ' '}`,
        x.closed ? null : h('button', { type: 'button', 'data-testid': `tp-close-${x.targetId}`, onclick: async () => {
          const c = await t.request('POST', `/admin/material-targets/${x.targetId}/close`, { body: {} });
          if (!live()) return;
          if (c.kind === 'http' && c.status === 200) { say('제공을 닫았어요. 이미 푼 기록은 그대로 남아요.'); void loadTargets(); } else say('닫지 못했어요. 다시 시도해 주세요.');
        } }, '제공 닫기')))));
  }

  const create = h('button', { type: 'button', class: 'primary', 'data-testid': 'tp-create', onclick: async () => {
    const pairs = picker.pairs();
    if (relSel.value === '' || pairs.length === 0) { say('자료와 학교-학년을 하나 이상 켜 주세요.'); return; }
    const body = { releaseId: relSel.value, pairs };
    const id = JSON.stringify(body);
    if (pendingKey === null || pendingKey.id !== id) pendingKey = { id, key: newRequestKey() };
    const r = await t.request('POST', '/admin/material-targets', { body, key: pendingKey.key });
    if (!live()) return;
    if (r.kind === 'network') { say('결과를 받지 못했어요. 같은 버튼을 다시 누르면 같은 요청으로 확인해요.'); return; }
    pendingKey = null;
    if (r.status === 201 || r.status === 200) {
      picker.reset(); // 선택기 변경 알림이 안내를 지우므로 먼저 끄고 안내를 쓴다
      say(`제공을 만들었어요. 지금 맞는 학생 ${String(r.body['includedStudents'])}명이 포함됐고, 앞으로 첫설정을 마치는 학생도 자동 포함돼요. 학생 배포 허용은 아직 꺼져 있어요.`);
      toast('제공을 만들었어요.'); void loadTargets();
    } else say(r.status === 403 ? '이 계정은 할 수 없는 작업이에요.' : r.status === 409 ? `만들지 못했어요(${String(r.body['error'])}).` : `만들지 못했어요(${String(r.body['error'] ?? r.status)}).`);
  } }, '이 조합에 자료 제공');

  clear(root);
  root.append(h('section', { 'data-testid': 'target-panel' }, h('h3', {}, '학교·학년별 자료 제공'),
    h('p', {}, '학교별로 학년 버튼을 눌러 켜고 꺼요. 켠 학교-학년 쌍에 해당하는 학생에게만 자료가 보이고, 학생은 그중에서 자기 자료를 직접 골라요.'),
    msg, h('label', {}, '자료 ', relSel), picker.node, role === 'director' ? h('div', {}, newName, ' ', newLevel, ' ', addSchool) : null, create, list));

  void (async () => {
    const r = await t.request('GET', '/admin/material-target-releases');
    if (!live()) return;
    if (r.kind !== 'http' || r.status !== 200) { say('학교·자료 목록을 불러오지 못했어요.'); return; }
    relSel.append(h('option', { value: '' }, '자료 선택'), ...(r.body['releases'] as Rel[]).map((x) => h('option', { value: x.id }, `${x.title} v${x.version}`)));
    await loadTargets();
  })();
}
